/** Section 5 — "Economic & Financial Framework and Revenue Model" (EN PDF). */
export const financialEn = {
  id: 'financial',
  previewCount: 4,
  navLabel: 'Financial model',
  eyebrow: 'Economic & financial framework',
  title: 'Economic & Financial Framework and Revenue Model',
  subtitle:
    'A commercially viable, scalable, and sustainable media business — with an indicative media revenue target of approximately USD 6.4 million annually.',
  blocks: [
    {
      icon: 'description',
      title: 'Purpose of the Framework',
      wide: true,
      paragraphs: [
        'This framework defines the economic and financial model of the Global Economic Media Initiative, its revenue sources, and the financial relationship between HOLOL, local agencies, and media partners, with the objective of establishing a commercially viable, scalable, and sustainable media business.',
        'The initiative is based on the development of an integrated economic media program that can initially operate in one country and subsequently be replicated across other markets and countries, creating increasing media and commercial value as the international network expands.',
      ],
    },
    {
      icon: 'account_balance_wallet',
      title: 'Economic Model of the Initiative',
      paragraphs: [
        'The economic model of the initiative is based on two principal areas of activity and revenue:',
      ],
      labeled: [
        {
          label: 'First: Media and advertising revenues',
          text: 'Revenues generated through sponsorships, advertising, media exposure, branded appearances, media packages, and other media-related commercial products associated with the program. This is the area through which the commercial relationship with agencies and media partners is established.',
        },
        {
          label: 'Second: Economic, investment, and business opportunities',
          text: 'Opportunities, transactions, investments, and strategic partnerships developed through HOLOL with investors, companies, institutions, and other relevant parties, under separate agreements directly between the concerned parties. These activities are independent of the media revenue-sharing relationship with the agency.',
        },
      ],
    },
    {
      icon: 'movie',
      title: 'Program Cost and Target Revenue',
      paragraphs: [
        'The financial model is based on a weekly program comprising approximately 50–52 episodes per year. Production costs will vary from one country to another depending on the production level, broadcasting channel, local market conditions, labor costs, technical requirements, filming requirements, and other production factors.',
        'For the purposes of an initial feasibility model, the following indicative production costs may be assumed:',
      ],
      stats: [
        { value: '50–52', label: 'Episodes per year' },
        { value: '$30K–$60K', label: 'Indicative cost per episode' },
        { value: '$1.5M–$3.0M', label: 'Annually for a 50-episode season' },
      ],
      closing: [
        'This range is an indicative planning assumption rather than a fixed or binding production price. Final production costs will be determined by the local agency and media channel according to the actual production model.',
        'The commercial objective is for media and advertising revenues to exceed production costs and generate an appropriate and sustainable profit margin for the participating parties.',
      ],
    },
    {
      type: 'table',
      icon: 'table_chart',
      title: 'Indicative Annual Media Revenue Model',
      wide: true,
      paragraphs: [
        'The program is designed with a broad range of media spaces and commercial opportunities that can be sold individually or combined into structured packages. The following represents an initial indicative model:',
      ],
      headers: ['Revenue source', 'Target / assumption', 'Indicative value', 'Indicative annual revenue'],
      rows: [
        ['Main Program Sponsor', '1', '400,000 USD', '400,000 USD'],
        ['Major Sector Sponsors', '10', '150,000 USD', '1,500,000 USD'],
        ['Specialized Sector Sponsors', '10', '100,000 USD', '1,000,000 USD'],
        ['Segment Sponsors', '15', '50,000 USD', '750,000 USD'],
        ['In-Program Advertising', 'Based on packages and volume', '—', '750,000 USD'],
        ['Commercial Break Advertising', 'Based on market rates and volume', '—', '750,000 USD'],
        ['Corporate Logo & Continuous Visibility', 'Based on annual packages', '—', '750,000 USD'],
        ['Custom Corporate Media & Advertising Content', 'Based on packages', '—', '500,000 USD'],
      ],
      footer: { label: 'Indicative target total', value: '6,400,000 USD annually' },
      notes: [
        'Potential sector sponsors may be drawn from areas directly related to the nature of HOLOL and the markets in which the initiative operates, including real estate development and investment, banking and finance, investment, telecommunications, technology, construction, building materials, engineering and design, furniture and fit-out, legal services, valuation and consulting, property management, tourism and hospitality, transportation and logistics, insurance, energy, and institutional and financial services, among others.',
        'Not all sectors are required in every country. The relevant sectors and sponsors will be selected according to the characteristics of each market, the program, and the media channel.',
      ],
    },
    {
      icon: 'redeem',
      title: 'Sponsor and Advertiser Benefits',
      paragraphs: [
        'The value of each sponsorship or media package will be determined according to the level of exposure and media services provided. Depending on the package, these may include:',
      ],
      bullets: [
        'Display of the company name and logo within the program.',
        'Opening and closing identification associated with the sponsored segment.',
        'Visibility within the episode and related promotional materials.',
        'Advertising placements during the program or commercial breaks.',
        'Sponsorship of a specific segment or economic topic.',
        'Professional media exposure for the company or its executive representative.',
        'Presentation of key company information and business activity during the appearance.',
        'Visibility across associated digital and media platforms.',
        'Custom media content and corporate coverage according to the selected package.',
        'Priority or additional benefits when participating in international media packages.',
      ],
      closing: [
        'Final commercial pricing will be determined by the agency and media channel according to market conditions, program reach, audience size, number and duration of appearances, and the commercial value of the program.',
        'All figures stated in this framework are indicative planning estimates and are not binding rates. Final pricing may be adjusted by each agency according to its local market, media channel, audience size, and the actual commercial value of the program.',
      ],
    },
    {
      icon: 'public',
      title: 'International Media Packages',
      paragraphs: [
        'International media packages represent a major commercial growth opportunity for the initiative, allowing companies and institutions to obtain integrated media exposure across multiple HOLOL programs and markets.',
        'For example, an international package may include participation across five programs in five different markets:',
      ],
      stats: [
        { value: '$1,000,000', label: 'Indicative annual package value' },
        { value: '5', label: 'Programs in 5 markets' },
        { value: '$200,000', label: 'Average per market / program' },
      ],
      listLabel: 'Depending on the package structure, the offer may include:',
      bullets: [
        'Exposure across five HOLOL programs in participating markets.',
        'In-program advertising and commercial-break placements according to the agreed volume.',
        'Corporate logo and brand visibility across the participating programs and related media materials.',
        'Sponsorship or branded presence within selected segments or economic topics.',
        'Professional media appearances for the company or its executives where appropriate.',
        'Dedicated corporate media and advertising content across the participating markets.',
        'Exposure across associated digital and media platforms.',
        "International media coverage connected with the company's activities and target markets.",
      ],
      closing:
        'The final allocation and settlement mechanism between participating markets and agencies will be determined under the international framework governing the network.',
    },
    {
      icon: 'handshake',
      title: 'Financial Relationship Between HOLOL and the Agency',
      paragraphs: [
        'The financial relationship between HOLOL and the local agency will be determined according to the cooperation model adopted in each market. The proposed reference model for shared media and advertising revenues is:',
      ],
      stats: [
        { value: '50%', label: 'HOLOL' },
        { value: '50%', label: 'Agency' },
      ],
      closing:
        'The final percentage will be contractually determined according to the structure of the partnership and the respective roles and responsibilities of each party.',
    },
    {
      icon: 'flag',
      title: "HOLOL's Role in Supporting the Host Country",
      paragraphs: [
        'Through HOLOL, the initiative provides the host country with a significant economic and media platform for presenting its economy, investment opportunities, projects, markets, and development priorities, while connecting the country with investors, companies, institutions, and international markets.',
        'This role is provided to the host country without direct financial compensation from the government to HOLOL, as part of the economic and national-service dimension of the initiative.',
        'In return, the initiative benefits from institutional cooperation and support provided by the relevant authorities, including official coordination, access to information, participation of government officials and relevant entities, and the logistical facilitation required to implement the program.',
      ],
      closing:
        'Any business opportunities, investments, or commercial activities subsequently developed between HOLOL and investors, companies, or other parties are handled under separate agreements between the relevant parties.',
    },
    {
      type: 'table',
      icon: 'monitoring',
      title: 'Initial Financial Indicators',
      headers: ['Indicator', 'Initial model'],
      rows: [
        ['Number of episodes', '50–52 per year'],
        ['Indicative production cost per episode', '30,000–60,000 USD'],
        ['Indicative annual production cost', '1.5–3.0 million USD'],
        ['Indicative target media revenue', 'Approximately 6.4 million USD annually'],
        ['Indicative international package', '1,000,000 USD across five markets'],
        ['Reference media revenue share', '50% HOLOL / 50% Agency'],
      ],
      notes: [
        'These figures remain indicative and may be adjusted according to the country, market conditions, media channel, production level, audience reach, and the actual commercial value of the program.',
      ],
    },
    {
      icon: 'insights',
      title: 'Financial Summary',
      wide: true,
      highlight: true,
      paragraphs: ['The financial model of the initiative is based on a clear commercial equation:'],
      flow: [
        'Local production funded through the local media market',
        'Sponsorship, advertising, and media services generate revenue',
        'Revenue covers production costs',
        'Profit is generated',
        'The model expands into additional markets and countries',
        'The international network gains greater media and commercial value',
        'Local and international revenues increase',
      ],
      closing:
        'The program therefore represents an integrated economic media business capable of being replicated and scaled internationally, rather than a conventional television program dependent on a single advertising source.',
    },
  ],
};
